import { useMemo, useState } from 'react'
import { Box, Breadcrumbs, Typography, Stack, Button, Chip } from '@mui/material'
import { Add } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import { listShipments, type ShipmentListItem } from '../../api/logistica'
import CreateShipmentDialog from './CreateShipmentDialog'
import ShipmentDetailDialog from './ShipmentDetailDialog'

const STATUS_LABEL = { Pregatita: 'Pregătită', Expediata: 'Expediată', InTranzit: 'În tranzit', Livrata: 'Livrată' } as const
const STATUS_COLOR = { Pregatita: 'default', Expediata: 'info', InTranzit: 'warning', Livrata: 'success' } as const

export default function ShipmentsPage() {
  const [createOpen, setCreateOpen] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)
  const { data, isLoading } = useQuery({ queryKey: ['logistica', 'expeditii'], queryFn: listShipments })

  const columns = useMemo<GridColDef<ShipmentListItem>[]>(() => [
    { field: 'number', headerName: 'Nr.', width: 140, renderCell: (p) => <code>{p.row.number}</code> },
    { field: 'orderNumber', headerName: 'Comandă', width: 140 },
    { field: 'carrierName', headerName: 'Transportator', flex: 1, minWidth: 160 },
    {
      field: 'status', headerName: 'Status', width: 130, sortable: false,
      renderCell: (p) => <Chip size="small" label={STATUS_LABEL[p.row.status]} color={STATUS_COLOR[p.row.status]} variant="outlined" />,
    },
    { field: 'transportCost', headerName: 'Cost transport', width: 130, type: 'number', valueFormatter: (v: number | null) => v == null ? '—' : v.toFixed(2).replace('.', ',') },
    { field: 'shippedAt', headerName: 'Expediată la', width: 160, valueFormatter: (v: string | null) => v ? new Date(v).toLocaleString('ro-RO') : '—' },
    { field: 'deliveredAt', headerName: 'Livrată la', width: 160, valueFormatter: (v: string | null) => v ? new Date(v).toLocaleString('ro-RO') : '—' },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Logistică</Typography>
        <Typography variant="inherit" color="text.primary">Expediții</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Expediții</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>Expediere nouă</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          onRowClick={(p) => setDetailId(p.row.id)}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>

      <CreateShipmentDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(id) => { setCreateOpen(false); setDetailId(id) }} />
      <ShipmentDetailDialog id={detailId} onClose={() => setDetailId(null)} />
    </Stack>
  )
}
